import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/admin/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-lavender-50 px-5">
      <div className="w-full max-w-sm rounded-[1.75rem] border border-lavender-700/10 bg-white p-8 shadow-soft" data-testid="admin-login-card">
        <img src="/assets/logo.png" alt="Pawtastic Cuts" className="h-10 w-auto" />
        <h1 className="mt-6 font-serif text-2xl text-stone-900">Admin sign in</h1>
        <p className="mt-1 text-sm text-stone-500">Manage services, photos, events & settings.</p>
        <form onSubmit={onSubmit} className="mt-7 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="admin-email">Email</Label>
            <Input
              id="admin-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email-input"
              placeholder="admin@pawtasticcuts.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-password">Password</Label>
            <Input
              id="admin-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password-input"
              placeholder="••••••••"
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
          )}
          <Button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit-button"
            className="w-full rounded-full bg-lavender-700 py-6 font-semibold hover:bg-lavender-800"
          >
            {busy ? <Loader2 size={18} className="animate-spin" /> : "Sign in"}
          </Button>
        </form>
        <a href="/" data-testid="admin-login-back-link" className="mt-6 block text-center text-xs text-stone-400 transition-colors hover:text-lavender-700">
          ← Back to the website
        </a>
      </div>
    </div>
  );
}
